import * as React from "react";
import { TrendingUp, TrendingDown, Activity, Info, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const indexComponents = [{
  id: "affordability",
  title: "Affordability",
  description: "Measures the ratio of median household income to median property prices, adjusted for current interest rates and typical mortgage terms.",
  value: "+4.2%",
  isPositive: true,
  score: 68
}, {
  id: "availability",
  title: "Availability",
  description: "Tracks active listings per capita, average days on market, and new construction completions against population growth.",
  value: "-1.5%",
  isPositive: false,
  score: 42
}, {
  id: "velocity",
  title: "Velocity",
  description: "Monitors the speed of transactions, clearance rates at auction, and the volume of off-market transfers.",
  value: "+8.7%",
  isPositive: true,
  score: 85
}];
export default function IndexDetails() {
  return <section id="index-details" className="bg-background">
      <div className="bg-black text-white py-24 px-6 md:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16">
            <h2 className="font-default text-3xl md:text-4xl font-semibold tracking-tight text-white">
              Index Methodology
            </h2>
            <p className="font-sans text-white/70 mt-4 max-w-2xl text-lg">
              A transparent breakdown of the core metrics that drive our market analysis, normalized for accurate historical comparison.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {}
            <div className="lg:col-span-7 flex flex-col gap-8">
              <div className="flex items-center gap-3 mb-2">
                <Activity className="size-5 text-white/50" />
                <h3 className="font-default text-xl font-medium text-white">
                  Component Breakdown
                </h3>
              </div>
              
              <div className="flex flex-col gap-6">
                {indexComponents.map((item, i) => <Card key={item.id} data-index={i} className="rounded-none border border-white/20 bg-transparent text-white shadow-none">
                    <CardHeader className="pb-4 flex flex-row items-start justify-between gap-4 space-y-0">
                      <div className="space-y-1.5">
                        <CardTitle className="font-default text-lg text-white">
                          {item.title}
                        </CardTitle>
                        <p className="font-sans text-sm text-white/70 leading-relaxed max-w-[90%]">
                          {item.description}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`font-mono text-[14px] font-medium flex items-center gap-1 ${item.isPositive ? "text-emerald-400" : "text-red-400"}`}>
                          {item.isPositive ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
                          {item.value}
                        </span>
                        <span className="font-mono text-xs text-white/50">
                          Score: {item.score}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Progress value={item.score} className="h-1.5 bg-white/10 rounded-none [&_[data-slot=progress-indicator]]:bg-white [&_[data-slot=progress-indicator]]:rounded-none" />
                    </CardContent>
                  </Card>)}
              </div>
            </div>

            {}
            <div className="lg:col-span-5 lg:sticky lg:top-24 flex flex-col gap-12">
              {}
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Info className="size-5 text-white/50" />
                  <h3 className="font-default text-xl font-medium text-white">
                    The Scoring Scale
                  </h3>
                </div>
                <p className="font-sans text-white/70 text-sm leading-relaxed">
                  Our index normalizes complex, multi-dimensional market data into a simple 0-100 scale. A score of 50 represents a perfectly balanced market. Scores above 50 indicate conditions favoring sellers, while scores below 50 favor buyers.
                </p>
                
                <div className="space-y-3 pt-2">
                  <div className="flex justify-between font-mono text-xs text-white/50">
                    <span>0 (Buyer)</span>
                    <span>50 (Balanced)</span>
                    <span>100 (Seller)</span>
                  </div>
                  <div className="h-2 w-full bg-gradient-to-r from-red-500 via-white/50 to-emerald-500 rounded-none" />
                  <div className="flex justify-between font-mono text-[10px] text-white/40 uppercase tracking-wider">
                    <span>High Supply</span>
                    <span>Equilibrium</span>
                    <span>High Demand</span>
                  </div>
                </div>
              </div>

              <Separator className="bg-white/20" />

              {}
              <div className="space-y-6">
                <h3 className="font-default text-xl font-medium text-white">
                  2010 Baseline Year
                </h3>
                <p className="font-sans text-white/70 text-sm leading-relaxed">
                  All metrics are benchmarked against the year 2010, which serves as our baseline (Index = 100). This specific year was chosen as it represents a period of relative macroeconomic stability following the global financial crisis, allowing for clear historical comparison and trend analysis across subsequent market cycles.
                </p>
                
                <div className="pt-4">
                  <Button asChild variant="outline" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black transition-colors">
                    <Link to="/MethodologyPage">
                      Read Full Methodology
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}